import { StyledMobileFilterForm } from './style';
import { Button } from '../../Button';
import { CategorySelector } from '../../Select/Selectors/CategorySelectors';
import { QualityMarkCheckboxes } from '../../Checkbox/Checkboxes/QualityMarkCheckboxes';
import { AdditionalOptionsCheckboxes } from '../../Checkbox/Checkboxes/AdditionalOptionsCheckboxes';
import { DeliveriesCheckboxes } from '../../Checkbox/Checkboxes/DeliveriesCheckboxes';
import { EmarketCheckbox } from '../../Checkbox/Checkboxes/EmarketCheckbox';
import { CooperativeCheckbox } from '../../Checkbox/Checkboxes/CooperativeCheckbox';
import { DynamicPurchaseCheckbox } from '../../Checkbox/Checkboxes/DynamicPurchaseCheckbox';
import { useEffect } from 'react';
import { Filter } from 'types/Filter';
import { FilterOptions } from 'types/FilterOptions';
import { Link } from 'react-router-dom';
import PublicRoute, { publicPath } from 'enums/publicRoutes';
import { LocationCheckboxes } from 'ui/Checkbox/Checkboxes/LocationCheckboxes';
import { FarmsCheckboxes } from 'ui/Checkbox/Checkboxes/FarmsCheckboxes';
import { DistanceRadiusSelector } from 'ui/Select/Selectors/DistanceRadiusSelector';
import { AddressSelector } from 'ui/Select/Selectors/AddressSelector';
import { useIntl } from 'react-intl';
import { getCategoryFieldIds } from 'utils/filterCategoryFields';
import useMarketType from 'utils/useMarketType';
import {
  applyDefaultDistanceRadius,
  clearAddressWhenLocationChosen,
} from 'utils/filterHelper';
import type { FieldData } from 'rc-field-form/lib/interface';

interface Props {
  setFilter: (newFilter: Filter) => void;
  filterOptions: FilterOptions;
  filter: Filter;
  onClearFilters?: () => void;
  onExport?: () => void;
  exportLoading?: boolean;
}
export const MobileFilterForm = ({
  setFilter,
  filterOptions,
  filter,
  onClearFilters,
  onExport,
  exportLoading,
}: Props) => {
  const [form] = StyledMobileFilterForm.useForm();
  const intl = useIntl();
  const categoryFieldIds = getCategoryFieldIds(filter);
  const { marketType } = useMarketType();
  const isWholesale = marketType === 'WHOLESALE';
  const hasLocation = Array.isArray(filter?.location)
    ? filter.location.length > 0
    : !!filter?.location;
  const hasAddress = !!filter?.address;

  useEffect(() => {
    if (!!filter) {
      const normalizedFilter: Filter = { ...filter } as Filter;

      if (
        normalizedFilter.farm !== undefined &&
        normalizedFilter.farm !== null
      ) {
        normalizedFilter.farm = Array.isArray(normalizedFilter.farm)
          ? normalizedFilter.farm
          : [normalizedFilter.farm];
      }

      if (
        normalizedFilter.location !== undefined &&
        normalizedFilter.location !== null
      ) {
        normalizedFilter.location = Array.isArray(normalizedFilter.location)
          ? normalizedFilter.location
          : [normalizedFilter.location];
      }

      form.setFieldsValue(normalizedFilter);
    }
  }, [form, filter]);

  const handleClearFilters = (): void => {
    form.resetFields();
    onClearFilters?.();
  };

  const handleFieldsChange = (changedFields?: FieldData[]) => {
    clearAddressWhenLocationChosen(form, changedFields);
    const values = applyDefaultDistanceRadius(form, marketType);
    setFilter(values);
  };

  return (
    <div className={'form-container'}>
      <StyledMobileFilterForm
        layout={'vertical'}
        onFieldsChange={handleFieldsChange}
        form={form}
      >
        <div className={'categories-form'}>
          <CategorySelector
            mainCategoryValue={categoryFieldIds.mainCategoryValue}
            subCategoryValue={categoryFieldIds.subCategoryValue}
            subSubCategoryValue={categoryFieldIds.subSubCategoryValue}
          />
          {marketType === 'WHOLESALE' && <DynamicPurchaseCheckbox />}
          {marketType !== 'WHOLESALE' && (
            <div className="products-navigate-list">
              <Link to={PublicRoute.PRODUCTS_ALL_DISCOUNTED}>
                {intl.formatMessage({ id: 'general.sales' })}
              </Link>
              <Link to={PublicRoute.PRODUCTS_ALL_NEW}>
                {intl.formatMessage({ id: 'general.news' })}
              </Link>
              <Link to={publicPath.giftSets()}>
                {intl.formatMessage({ id: 'general.gift_sets' })}
              </Link>
            </div>
          )}
          {filterOptions?.emarket && marketType !== 'WHOLESALE' && (
            <EmarketCheckbox emarket={filterOptions.emarket} />
          )}
          <CooperativeCheckbox cooperative={filterOptions?.cooperative} />
          <FarmsCheckboxes farms={filterOptions?.farms} />
          <LocationCheckboxes locations={filterOptions?.locations} />
          {isWholesale && (
            <div className={'category-sel-wrap'}>
              <AddressSelector form={form} onChange={handleFieldsChange} />
            </div>
          )}
          {isWholesale && (hasLocation || hasAddress) && (
            <DistanceRadiusSelector controls={true} />
          )}
          <QualityMarkCheckboxes qmarks={filterOptions?.qmark} />
          <AdditionalOptionsCheckboxes services={filterOptions?.services} />
          {marketType !== 'WHOLESALE' && (
            <DeliveriesCheckboxes delivery={filterOptions?.delivery} />
          )}
          {onClearFilters && (
            <div className={'clear-filters-button-wrapper'}>
              <Button
                type={'default'}
                htmlType={'button'}
                block
                onClick={handleClearFilters}
                label={intl.formatMessage({ id: 'general.delete_filters' })}
              />
            </div>
          )}
          {marketType === 'WHOLESALE' && onExport && (
            <div className={'export-product-list-wrapper'}>
              <Button
                type={'default'}
                block
                onClick={onExport}
                disabled={exportLoading}
                loading={exportLoading}
                label={
                  exportLoading
                    ? intl.formatMessage({ id: 'general.loading' })
                    : intl.formatMessage({ id: 'products.save_list' })
                }
              />
            </div>
          )}
        </div>
      </StyledMobileFilterForm>
    </div>
  );
};
